/**
 * <countdown-bar> — persistent, self-restarting countdown for the announcement bar.
 *
 * Behaviour (per spec):
 * - Duration is configured in hours via the `data-duration-hours` attribute (Theme Customizer).
 * - The end time is persisted in localStorage, so a page refresh continues from the
 *   remaining time instead of restarting.
 * - When the countdown reaches zero it resets and starts again from the full duration.
 * - Changing the configured duration in the Customizer restarts the countdown.
 */
class CountdownBar extends HTMLElement {
  connectedCallback() {
    this.durationMs = Math.max(0, parseFloat(this.dataset.durationHours) || 0) * 60 * 60 * 1000;
    this.storageKey = this.dataset.storageKey || 'nurture-countdown-bar';

    this.fields = {
      days: this.querySelector('[data-countdown="days"]'),
      hours: this.querySelector('[data-countdown="hours"]'),
      minutes: this.querySelector('[data-countdown="minutes"]'),
      seconds: this.querySelector('[data-countdown="seconds"]'),
    };

    if (!this.durationMs) return;

    this.end = this.resolveEnd();
    this.render();
    this.timer = window.setInterval(() => this.render(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  /**
   * Returns the stored end timestamp, creating (or resetting) it when it is
   * missing, expired, or was created for a different configured duration.
   */
  resolveEnd() {
    const now = Date.now();
    let stored = null;

    try {
      stored = JSON.parse(window.localStorage.getItem(this.storageKey));
    } catch (error) {
      stored = null;
    }

    const valid =
      stored &&
      stored.duration === this.durationMs &&
      typeof stored.end === 'number' &&
      stored.end > now;

    if (valid) return stored.end;

    const end = now + this.durationMs;
    this.persist(end);
    return end;
  }

  persist(end) {
    try {
      window.localStorage.setItem(
        this.storageKey,
        JSON.stringify({ end, duration: this.durationMs })
      );
    } catch (error) {
      /* storage unavailable — countdown still works for the session */
    }
  }

  render() {
    let remaining = this.end - Date.now();

    // Reached zero: reset and start again from the full duration.
    if (remaining <= 0) {
      this.end = Date.now() + this.durationMs;
      this.persist(this.end);
      remaining = this.durationMs;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.setField('days', days);
    this.setField('hours', hours);
    this.setField('minutes', minutes);
    this.setField('seconds', seconds);
  }

  setField(name, value) {
    const el = this.fields[name];
    if (el) el.textContent = String(value).padStart(2, '0');
  }
}

if (!customElements.get('countdown-bar')) {
  customElements.define('countdown-bar', CountdownBar);
}
